import { customBaseUrl, httpGet, httpPost } from '../store/http';

// ────────────────────────────────────────────────────────────────────────────
// Audit Trail Service
// Base URL: customBaseUrl.auditUrl → /audit-reporting
//
// Swagger docs:
//   https://servicesv3.staging.wayabank.ng/audit-reporting/swagger-ui/index.html
// ────────────────────────────────────────────────────────────────────────────

const BASE = () => customBaseUrl.auditUrl;

/**
 * Build a query string from params, ignoring null/undefined/empty values.
 */
const buildQuery = (params = {}) => {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== null && v !== undefined && v !== ''
  );
  if (!entries.length) return '';
  return '?' + entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&');
};

// ═══════════════════════════════════════════════════════════════════════════
// 1. FETCH ALL AUDIT EVENTS (paginated)
//    GET /audit/events?page=0&size=10&sortBy=timestamp&sortDir=desc
// ═══════════════════════════════════════════════════════════════════════════
export const getAuditTrailLogs = async (params = {}) => {
  const { page = 0, size = 10, sortBy = 'timestamp', sortDir = 'desc' } = params;
  const query = buildQuery({ page, size, sortBy, sortDir });
  return await httpGet(`${BASE()}/audit/events${query}`);
};

// ═══════════════════════════════════════════════════════════════════════════
// 1b. FETCH ALL ADMIN AUDIT EVENTS (paginated)
//     GET /audit/admin/events/all
// ═══════════════════════════════════════════════════════════════════════════
export const getAdminAuditEventsAll = async (params = {}) => {
  const { page = 0, size = 10, sortBy = 'timestamp', sortDir = 'desc' } = params;
  const query = buildQuery({ page, size, sortBy, sortDir });
  const res = await httpGet(`${BASE()}/audit/admin/events/all${query}`);

  // Standardize result structure
  if (res?.success) {
    if (res.data?.content) return res;
    if (Array.isArray(res.data)) {
      return {
        ...res,
        data: {
          content: res.data,
          totalElements: res.data.length,
        },
      };
    }
  }
  return res;
};

// ═══════════════════════════════════════════════════════════════════════════
// 2. SEARCH AUDIT EVENTS (filtered)
//    GET /audit/events/search?userId=&eventType=&entityType=&status=&startTime=&endTime=&page=0&size=10
// ═══════════════════════════════════════════════════════════════════════════
export const searchAuditEvents = async (params = {}) => {
  const {
    page = 0,
    size = 10,
    sortBy = 'timestamp',
    sortDir = 'desc',
    userId,
    eventType,
    entityType,
    status,
    startTime,
    endTime,
  } = params;
  const query = buildQuery({
    page, size, sortBy, sortDir,
    userId, eventType, entityType, status, startTime, endTime,
  });
  const res = await httpGet(`${BASE()}/audit/events/search${query}`);
  
  // Standardize result structure
  if (res?.success) {
    if (res.data?.content) return res; // Already standardized
    if (Array.isArray(res.data)) {
      return {
        ...res,
        data: {
          content: res.data,
          totalElements: res.data.length,
        },
      };
    }
  }
  return res;
};

// ═══════════════════════════════════════════════════════════════════════════
// 3. GET EVENTS BY EVENT TYPE (non-paginated list)
//    GET /audit/events/type/{eventType}
// ═══════════════════════════════════════════════════════════════════════════
export const getAuditEventsByType = async (eventType) => {
  const res = await httpGet(`${BASE()}/audit/events/type/${encodeURIComponent(eventType)}`);
  // If result is a flat list, wrap it for pagination compatibility
  if (res?.success && Array.isArray(res.data)) {
    return {
      ...res,
      data: {
        content: res.data,
        totalElements: res.data.length,
      },
    };
  }
  return res;
};

// ═══════════════════════════════════════════════════════════════════════════
// 4. GET SINGLE EVENT DETAIL
//    GET /audit/events/{id}
// ═══════════════════════════════════════════════════════════════════════════
export const getAuditTrailDetail = async (id) => {
  return await httpGet(`${BASE()}/audit/events/${id}`);
};

// ═══════════════════════════════════════════════════════════════════════════
// 5. GET EVENTS BY USER
//    GET /audit/events/user/{userId}           (non-paginated)
//    GET /audit/events/user/{userId}/paginated (paginated)
// ═══════════════════════════════════════════════════════════════════════════
export const getAuditEventsByUser = async (userId) => {
  return await httpGet(`${BASE()}/audit/events/user/${encodeURIComponent(userId)}`);
};

export const getAuditEventsByUserPaginated = async (userId, params = {}) => {
  const { page = 0, size = 10, sortBy = 'timestamp', sortDir = 'desc' } = params;
  const query = buildQuery({ page, size, sortBy, sortDir });
  const res = await httpGet(`${BASE()}/audit/events/user/${encodeURIComponent(userId)}/paginated${query}`);
  
  // Standardize result structure
  if (res?.success) {
    if (res.data?.content) return res; // Already standardized
    if (Array.isArray(res.data)) {
      return {
        ...res,
        data: {
          content: res.data,
          totalElements: res.data.length,
        },
      };
    }
  }
  return res;
};

// ═══════════════════════════════════════════════════════════════════════════
// 6. GET EVENTS BY ENTITY
//    GET /audit/events/entity/{entityType}/{entityId}
// ═══════════════════════════════════════════════════════════════════════════
export const getAuditEventsByEntity = async (entityType, entityId) => {
  const res = await httpGet(
    `${BASE()}/audit/events/entity/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`
  );
  // If result is a flat list, wrap it for pagination compatibility
  if (res?.success && Array.isArray(res.data)) {
    return {
      ...res,
      data: {
        content: res.data,
        totalElements: res.data.length,
      },
    };
  }
  return res;
};

// ═══════════════════════════════════════════════════════════════════════════
// 7. REPORTING: STATISTICS
//    GET /reports/statistics   → detailed stats (by user, type, status, date range)
//    GET /reports/system-health → totalEvents, successEvents, failedEvents, successRate
// ═══════════════════════════════════════════════════════════════════════════
export const getAuditReportStatistics = async () => {
  return await httpGet(`${BASE()}/reports/statistics`);
};

export const getAuditSystemHealth = async () => {
  return await httpGet(`${BASE()}/reports/system-health`);
};

// Combined stats: merges system-health + statistics for the dashboard cards
export const getAuditTrailStats = async () => {
  try {
    const [healthRes, statsRes] = await Promise.all([
      getAuditSystemHealth(),
      getAuditReportStatistics(),
    ]);

    const health = healthRes?.success ? healthRes.data : {};
    const stats = statsRes?.success ? statsRes.data : {};

    // Count failed logins from eventsByType + eventsByStatus
    const eventsByType = stats?.eventsByType || {};
    const eventsByStatus = stats?.eventsByStatus || {};

    return {
      success: true,
      data: {
        total: health.totalEvents || stats.totalEvents || 0,
        adminActions: 0,        // Not directly available — will be derived client-side
        customerActions: 0,     // Not directly available — will be derived client-side
        failedLogins: eventsByStatus.FAILED || 0,
        systemChanges: (eventsByType.UPDATE_CONFIGURATION || 0) +
                        (eventsByType.KYC_TIER_DOWNGRADE || 0) +
                        (eventsByType.KYC_TIER_RESTORED || 0),
        loginAttempts: eventsByType.USER_LOGIN || 0,
        successRate: health.successRate || 0,
        recentEvents24h: health.recentEvents24h || 0,
        eventsByType,
        eventsByStatus,
      },
    };
  } catch (err) {
    console.error('Failed to fetch combined audit stats:', err);
    return { success: false, data: {} };
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// 8. REPORTING: USER ACTIVITY
//    GET /reports/user-activity/{userId}?startDate=&endDate=
// ═══════════════════════════════════════════════════════════════════════════
export const getUserActivityReport = async (userId, startDate, endDate) => {
  const query = buildQuery({ startDate, endDate });
  return await httpGet(`${BASE()}/reports/user-activity/${encodeURIComponent(userId)}${query}`);
};

// ═══════════════════════════════════════════════════════════════════════════
// 9. REPORTING: EXPORT
//    POST /reports/export  { reportType, userId, eventType, entityType,
//                            startDate, endDate, status, format }
//    NOTE: Backend returns a placeholder — "to be implemented"
// ═══════════════════════════════════════════════════════════════════════════
export const exportAuditTrailLogs = async (params = {}) => {
  return await httpPost(`${BASE()}/reports/export`, params);
};

// ═══════════════════════════════════════════════════════════════════════════
// 10. REPORTING: DETAILED REPORT (paginated)
//     POST /reports/detailed?page=0&size=10&sortBy=timestamp&sortDir=desc
//     Body: { reportType, userId, eventType, entityType, startDate, endDate, status }
//     NOTE: Backend has a SQL bug with timestamp parameters
// ═══════════════════════════════════════════════════════════════════════════
export const getDetailedReport = async (body = {}, params = {}) => {
  const { page = 0, size = 10, sortBy = 'timestamp', sortDir = 'desc' } = params;
  const query = buildQuery({ page, size, sortBy, sortDir });
  return await httpPost(`${BASE()}/reports/detailed${query}`, body);
};

// ═══════════════════════════════════════════════════════════════════════════
// 11. REPORTING: SUMMARY REPORT
//     POST /reports/summary
//     Body: { reportType, userId, eventType, entityType, startDate, endDate, status }
//     NOTE: Backend returns error — "to be implemented"
// ═══════════════════════════════════════════════════════════════════════════
export const getSummaryReport = async (body = {}) => {
  return await httpPost(`${BASE()}/reports/summary`, body);
};

// ═══════════════════════════════════════════════════════════════════════════
// Convenience functions used by the tabs
// These wrap getAuditTrailLogs to filter by event type on the frontend
// since the backend /audit/events/search has a SQL bug with some params
// ═══════════════════════════════════════════════════════════════════════════

/** Fetch login-related audit logs */
export const getLoginAuditLogs = async (params = {}) => {
  // Use the main paginated endpoint; filter by eventType on frontend if search is broken
  return getAuditTrailLogs(params);
};

/** Fetch admin-only audit logs (no backend filter exists yet) */
export const getAdminAuditLogs = async (params = {}) => {
  return getAuditTrailLogs(params);
};

/** Fetch customer-only audit logs (no backend filter exists yet) */
export const getCustomerAuditLogs = async (params = {}) => {
  return getAuditTrailLogs(params);
};

/** Fetch system change logs (no backend filter exists yet) */
export const getSystemChangeAuditLogs = async (params = {}) => {
  return getAuditTrailLogs(params);
};
